/**
 * uploadPhoto — POST /api/projects/detaildocuments/upload-photo/.
 * Multipart. applyLocal stashes File blobs into visit_file_queue
 * (the canonical local-side record of the upload) so the sync engine
 * can re-attempt without recomputing the blob. sync() reads the queue
 * rows back, builds FormData, and POSTs. If a visitLocalId is supplied,
 * the op depends on the parent visit having synced so calllog_id can
 * be included in the multipart body.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getByIndex } from '../../utils/indexedDB';
import {
  BASE_URL,
  multipartHeaders,
  classifyResponse,
  networkErrorResult,
  resolveParentCallLogId,
  blobToArrayBuffer,
} from '../httpHelpers';

export interface UploadPhotoInput {
  visitLocalId?: string;
  projectId: number;
  storeId: number;
  files: File[];
  date?: string; // YYYY-MM-DD
}

export interface UploadPhotoResponse {
  success: boolean;
  document_ids: number[];
}

interface FileQueueRow {
  id?: number;
  taskUUID: string; // the OP localId (joins file_queue rows to this op)
  type: 'photo' | 'document';
  // Raw bytes — stored as ArrayBuffer rather than Blob so the row
  // survives IDB structured-clone in both real browsers (where Blob
  // would also work) and the fake-indexeddb test runtime (which
  // doesn't round-trip Blob fidelity through jsdom). Reconstructed
  // into a Blob at sync time via the mimeType field.
  fileData: ArrayBuffer;
  fileName: string;
  mimeType: string;
  syncStatus: 'pending' | 'synced' | 'failed';
  createdAt: string;
}

export const uploadPhotoOp: WriteOp<UploadPhotoInput, UploadPhotoInput, UploadPhotoResponse> = {
  kind: 'uploadPhoto',

  async applyLocal(input, localId) {
    const now = new Date().toISOString();
    for (const file of input.files) {
      const fileData = await blobToArrayBuffer(file);
      const row: FileQueueRow = {
        taskUUID: localId, // join key — sync() looks up by this
        type: 'photo',
        fileData,
        fileName: file.name,
        mimeType: file.type || 'image/jpeg',
        syncStatus: 'pending',
        createdAt: now,
      };
      await put(STORES.VISIT_FILE_QUEUE, row);
    }
  },

  serialize(input, localId) {
    return {
      action: 'uploadPhoto',
      endpoint: '/api/projects/detaildocuments/upload-photo/',
      method: 'POST',
      // payload omits the actual blobs (they live in visit_file_queue);
      // sync() reads them back by relatedTaskUUID.
      payload: {
        visitLocalId: input.visitLocalId,
        projectId: input.projectId,
        storeId: input.storeId,
        date: input.date,
        files: [] as File[], // placeholder — blobs are in file_queue
      },
      priority: 60,
      maxRetries: 5,
      dependsOn: input.visitLocalId ?? null,
      relatedTaskUUID: localId,
      // No Idempotency-Key on multipart; server-side dedup uses upload
      // metadata (calllog_id + file checksum) instead.
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as UploadPhotoInput;
    let calllogid: number | null = null;
    if (input.visitLocalId) {
      const resolved = await resolveParentCallLogId(input.visitLocalId);
      if (resolved === undefined) {
        return {
          status: 'pending',
          retriable: true,
          errorMessage: 'Parent visit has not synced yet',
        };
      }
      calllogid = resolved;
    }

    const localId = row.relatedTaskUUID;
    if (!localId) {
      return { status: 'failed', retriable: false, errorMessage: 'uploadPhoto missing localId' };
    }
    const fileRows = (await getByIndex<FileQueueRow>(
      STORES.VISIT_FILE_QUEUE,
      'taskUUID',
      localId,
    )) as FileQueueRow[];
    const pending = fileRows.filter((r) => r.syncStatus === 'pending');
    if (pending.length === 0) {
      // Already uploaded on a prior attempt; treat as idempotent success.
      return { status: 'success', retriable: false };
    }

    const formData = new FormData();
    formData.append('project_id', String(input.projectId));
    formData.append('store_id', String(input.storeId));
    if (calllogid != null) formData.append('calllog_id', String(calllogid));
    if (input.date) formData.append('date', input.date);
    for (const r of pending) {
      const blob = new Blob([r.fileData], { type: r.mimeType });
      formData.append('documents', blob, r.fileName);
    }

    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${row.endpoint}`, {
        method: row.method,
        headers: multipartHeaders(),
        body: formData,
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile(row) {
    const localId = row.relatedTaskUUID;
    if (!localId) return;
    const fileRows = (await getByIndex<FileQueueRow>(
      STORES.VISIT_FILE_QUEUE,
      'taskUUID',
      localId,
    )) as FileQueueRow[];
    for (const r of fileRows) {
      await put(STORES.VISIT_FILE_QUEUE, { ...r, syncStatus: 'synced' });
    }
  },

  dependsOn(input) {
    return input.visitLocalId ?? null;
  },
};

export type UploadPhotoOp = typeof uploadPhotoOp;
